import { formatDate } from "./dateUtils";
import {
	buildPharmacyItemBillingFieldsMap,
	buildSectionBillingItemFields,
	convertLineAmount,
	formatBillingDiscountType,
	getMaxItemTaxPercentage,
} from "./billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";

const toDisplay = (value) => toMoneyNumber(value);

const buildInvoiceSectionBilling = ({
	prefix,
	billingSummary = {},
	taxConfig = {},
	posTaxType = "gst",
	discountType,
	discountValue,
}) => {
	const taxType = String(taxConfig?.taxType || posTaxType || "gst").toLowerCase();
	const resolvedDiscountType = discountType ?? billingSummary[`${prefix}_discount_type`];
	const resolvedDiscountValue =
		discountValue ?? billingSummary[`${prefix}_discount_value`];

	return {
		...buildSectionBillingItemFields({
			discountType: resolvedDiscountType,
			discountValue: resolvedDiscountValue,
			taxType,
			taxPercentage: taxConfig?.taxPercentage ?? 0,
			subTotal: billingSummary[`${prefix}_net_taxable`] ?? 0,
			netTotal: billingSummary[`${prefix}_total`] ?? 0,
		}),
		lineSubtotal: toDisplay(billingSummary[`${prefix}_subtotal`] ?? 0),
		discountAmount: toDisplay(billingSummary[`${prefix}_discount`] ?? 0),
		taxAmount: toDisplay(billingSummary[`${prefix}_tax`] ?? 0),
		CGST: toDisplay(billingSummary[`${prefix}_CGST`] ?? 0),
		SGST: toDisplay(billingSummary[`${prefix}_SGST`] ?? 0),
		IGST: toDisplay(billingSummary[`${prefix}_IGST`] ?? 0),
		VAT: toDisplay(billingSummary[`${prefix}_VAT`] ?? 0),
		cgstPercentage: taxConfig?.cgstPercentage ?? 0,
		sgstPercentage: taxConfig?.sgstPercentage ?? 0,
	};
};

export const buildBillingInvoiceData = ({
	response,
	markAsPaid = false,
	activePrescription,
	selectedPatient,
	cart = [],
	itemTaxes = {},
	serviceItems = [],
	investigationItems = [],
	billingSummary = {},
	payment = {},
	paymentBalance = {},
	totals = {},
	convertCurrency,
	sourceCurrency,
	getCurrencySymbol,
	pharmacyPOS,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	discountType = "Percentage",
	discountValue = 0,
	transportCost = 0,
	patientDisplayName = "",
	servicesTaxConfig = {},
	investigationsTaxConfig = {},
}) => {
	const payCurrency = payment.currency || sourceCurrency || "INR";
	const pharmacyCurrency = sourceCurrency || payCurrency;
	const currencySymbol = getCurrencySymbol?.(payCurrency) || payCurrency;

	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const convertToPay = (amount, fromCurrency = pharmacyCurrency) =>
		convertLineAmount(amount, fromCurrency, payCurrency, convertCurrency, pharmacyCurrency);

	const salesData = response?.data?.data?.sales;
	const receiptQrData = response?.data?.data?.paymentQR;

	const pharmacyBilling = buildInvoiceSectionBilling({
		prefix: "pharmacy",
		billingSummary,
		taxConfig: {
			taxType: posTaxType,
			taxPercentage: getMaxItemTaxPercentage(itemTaxes),
		},
		posTaxType,
		discountType,
		discountValue,
	});

	const servicesBilling = buildInvoiceSectionBilling({
		prefix: "services",
		billingSummary,
		taxConfig: servicesTaxConfig,
		posTaxType,
	});

	const investigationsBilling = buildInvoiceSectionBilling({
		prefix: "investigation",
		billingSummary,
		taxConfig: investigationsTaxConfig,
		posTaxType,
	});

	const pharmacyItemBillingFields = buildPharmacyItemBillingFieldsMap({
		cart,
		itemTaxes,
		discountType,
		discountValue,
		totals,
		posTaxType,
		isSameState,
		targetCurrency: payCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies: [],
	});

	const pharmacyItems = cart.map((item) => {
		const itemKey = `${item._id}-${item.selectedBatchId}`;
		const taxInfo = itemTaxes[itemKey] || {};
		const unitPrice = convertToPay(item.salePrice || 0);
		const lineTotal = toDisplay(unitPrice * (item.quantity || 0));
		const itemBilling = pharmacyItemBillingFields.get(itemKey) || {};

		let itemCGSTPercentage = 0;
		let itemSGSTPercentage = 0;
		let itemIGSTPercentage = 0;

		if (posTaxType === "gst") {
			if (isSameState) {
				itemCGSTPercentage = (taxInfo.taxPercentage || 0) / 2;
				itemSGSTPercentage = (taxInfo.taxPercentage || 0) / 2;
			} else {
				itemIGSTPercentage = taxInfo.taxPercentage || 0;
			}
		}

		return {
			_id: item._id,
			brandName: item.brandName || item.saltName,
			saltName: item.saltName,
			quantity: item.quantity || 0,
			salePrice: unitPrice,
			batchNumber: item.batchNumber,
			productForm: item.productForm,
			taxAmount: convertToPay(taxInfo.tax || 0),
			taxPercentage: taxInfo.taxPercentage || 0,
			CGST: convertToPay(taxInfo.CGST || 0),
			SGST: convertToPay(taxInfo.SGST || 0),
			IGST: convertToPay(taxInfo.IGST || 0),
			VAT: convertToPay(taxInfo.VAT || 0),
			CGSTPercentage: itemCGSTPercentage,
			SGSTPercentage: itemSGSTPercentage,
			IGSTPercentage: itemIGSTPercentage,
			lineTotal,
			...itemBilling,
		};
	});

	const maxTaxPercentage = Math.max(
		0,
		...pharmacyItems.map((item) => toMoneyNumber(item.tax || item.taxPercentage))
	);

	let CGSTPercentage = 0;
	let SGSTPercentage = 0;
	let IGSTPercentage = 0;

	if (posTaxType === "gst") {
		if (isSameState) {
			CGSTPercentage = maxTaxPercentage / 2;
			SGSTPercentage = maxTaxPercentage / 2;
		} else {
			IGSTPercentage = maxTaxPercentage;
		}
	}

	const pharmacySubtotal = convertToPay(totals.subtotal || 0);
	const pharmacyDiscount = convertToPay(totals.discount || 0);
	const pharmacyTax = convertToPay(totals.tax || 0);
	const pharmacyCGST = convertToPay(totals.CGST || 0);
	const pharmacySGST = convertToPay(totals.SGST || 0);
	const pharmacyIGST = convertToPay(totals.IGST || 0);
	const pharmacyVAT = convertToPay(totals.VAT || 0);
	const pharmacyNetTotal = convertToPay(totals.netTotal || 0);
	const pharmacyLineTotal = toDisplay(billingSummary.pharmacy_line_total || 0);
	const pharmacyTransport = convertToPay(transportCost || 0);

	const mapServiceItem = (item) => {
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const lineAmount = convertToPay(item.amount, item.currency);
		return {
			name: item.service_name,
			quantity,
			unitPrice: quantity > 0 ? toDisplay(lineAmount / quantity) : 0,
			amount: lineAmount,
			isConsultation: !!item.isConsultation,
			currency: payCurrency,
			...servicesBilling,
		};
	};

	const consultationItems = serviceItems
		.filter((item) => item.isConsultation)
		.map(mapServiceItem);
	const generalServiceItems = serviceItems
		.filter((item) => !item.isConsultation)
		.map(mapServiceItem);

	const services = [...consultationItems, ...generalServiceItems];

	const investigations = investigationItems.map((item) => {
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const lineAmount = convertToPay(item.amount, item.currency);
		return {
			name: item.test_name,
			quantity,
			unitPrice: quantity > 0 ? toDisplay(lineAmount / quantity) : 0,
			amount: lineAmount,
			currency: payCurrency,
			...investigationsBilling,
		};
	});

	const servicesTotal = toDisplay(billingSummary.services_total || 0);
	const investigationsTotal = toDisplay(billingSummary.investigation_total || 0);
	const grandTotal = toDisplay(billingSummary.grand_total || paymentBalance.grandTotal || 0);
	const paidAmount = markAsPaid
		? grandTotal
		: toDisplay(Number(payment.amount_received) || paymentBalance.received || 0);
	const dueAmount = markAsPaid ? 0 : Math.max(0, grandTotal - paidAmount);
	const changeAmount = Math.max(0, paidAmount - grandTotal);

	const patientName =
		patientDisplayName ||
		activePrescription?.patient?.name ||
		selectedPatient?.name ||
		selectedPatient?.full_name ||
		"N/A";

	const paymentStatus = markAsPaid
		? "paid"
		: dueAmount <= 0 && paidAmount > 0
			? "paid"
			: paidAmount <= 0
				? "unpaid"
				: "partial_paid";
	const paymentStatusLabel =
		paymentStatus === "paid"
			? "Paid"
			: paymentStatus === "partial_paid"
				? "Partial Paid"
				: "Unpaid";

	return {
		invoiceNo: salesData?.salesNumber || activePrescription?.appointment_invoice?.invoice_number || "-",
		salesNumber: salesData?.salesNumber || null,
		date: new Date().toISOString(),
		customer: patientName,
		healthId:
			activePrescription?.patient?.health_id || selectedPatient?.health_id || "",
		prescriptionNo: activePrescription?.appointment_invoice?.invoice_number || "",
		doctorName: activePrescription?.doctor?.name || "",
		visitDate: formatDate(activePrescription?.createdAt),
		hospitalName: activePrescription?.hospital?.name || pharmacyPOS?.hospital?.name || "",
		consultationReason: activePrescription?.consultation_reason || "",

		currency: payCurrency,
		currencySymbol,
		paymentType: payment.payment_method || "cash",
		paymentStatus,
		paymentStatusLabel,
		settledAsPaid: !!markAsPaid,

		pharmacy: {
			items: pharmacyItems,
			billing: pharmacyBilling,
			subtotal: pharmacySubtotal,
			lineTotal: pharmacyLineTotal,
			discount: pharmacyDiscount,
			netTaxable: toDisplay(pharmacyBilling.sub_total),
			tax: pharmacyTax,
			CGST: pharmacyCGST,
			SGST: pharmacySGST,
			IGST: pharmacyIGST,
			VAT: pharmacyVAT,
			transport: pharmacyTransport,
			netTotal: pharmacyNetTotal,
		},

		services: {
			items: services,
			consultationItems,
			generalServiceItems,
			billing: servicesBilling,
			total: servicesTotal,
		},

		investigations: {
			items: investigations,
			billing: investigationsBilling,
			total: investigationsTotal,
		},

		summary: {
			pharmacyTaxable: toDisplay(billingSummary.pharmacy_taxable_total ?? billingSummary.pharmacy_total ?? 0),
			servicesTotal,
			investigationsTotal,
			subtotal: toDisplay(billingSummary.subtotal || 0),
			discount: toDisplay(billingSummary.discount || 0),
			tax: toDisplay(billingSummary.tax || 0),
			transport: toDisplay(billingSummary.transport || 0),
			grandTotal,
			paid: paidAmount,
			due: dueAmount,
			change: changeAmount,
		},

		discountMeta: {
			type: discountType,
			value: discountValue,
			discount_type: formatBillingDiscountType(discountType),
		},

		isSameState,
		taxType: (posTaxType || "gst").toUpperCase(),
		taxPercentage: maxTaxPercentage,
		CGSTPercentage,
		SGSTPercentage,
		IGSTPercentage,
		CGST: pharmacyCGST,
		SGST: pharmacySGST,
		IGST: pharmacyIGST,
		VAT: pharmacyVAT,
		tax: pharmacyTax,

		pharmacyStateCode,
		patientStateCode,
		receiptQrData,
	};
};
